import React, { useState, useEffect } from 'react'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { imageUploadHandler, type UploadedImage } from './ImageUploadHandler'
import { Upload, Image as ImageIcon, Clock, Search, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { cn } from '@/lib/utils'
import { useDrawer } from '@/components/ui/drawer'

interface ImagePickerDrawerProps {
  onImageSelect: (url: string, alt?: string, width?: string) => void
  onImageUpload?: (file: File) => Promise<string>
}

export const ImagePickerDrawer: React.FC<ImagePickerDrawerProps> = ({
  onImageSelect,
  onImageUpload,
}) => {
  const { closeDrawer } = useDrawer()
  const [selectedTab, setSelectedTab] = useState('upload')
  const [imageUrl, setImageUrl] = useState('')
  const [imageAlt, setImageAlt] = useState('')
  const [imageWidth, setImageWidth] = useState('100%')
  const [galleryImages, setGalleryImages] = useState<UploadedImage[]>([])
  const [recentImages, setRecentImages] = useState<UploadedImage[]>([])
  const [selectedImage, setSelectedImage] = useState<UploadedImage | null>(null)
  const [loading, setLoading] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [uploading, setUploading] = useState(false)

  useEffect(() => {
    loadGalleryImages()
    loadRecentImages()
  }, [])

  const loadGalleryImages = async () => {
    try {
      setLoading(true)
      const response = await imageUploadHandler.getImageGallery({
        limit: 50,
        offset: 0,
      })
      setGalleryImages(response.images || [])
    } catch (error) {
      console.error('Failed to load gallery images:', error)
    } finally {
      setLoading(false)
    }
  }

  const loadRecentImages = async () => {
    try {
      const response = await imageUploadHandler.getImageGallery({
        limit: 10,
        offset: 0,
      })
      setRecentImages(response.images || [])
    } catch (error) {
      console.error('Failed to load recent images:', error)
    }
  }

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file')
      return
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB')
      return
    }

    try {
      setUploading(true)
      const url = onImageUpload
        ? await onImageUpload(file)
        : await imageUploadHandler.uploadImage(file)

      onImageSelect(url, '', imageWidth)
      closeDrawer('image-picker')
      toast.success('Image uploaded successfully')
    } catch (error) {
      console.error('Upload failed:', error)
      toast.error('Failed to upload image')
    } finally {
      setUploading(false)
    }
  }

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()

    const file = e.dataTransfer.files[0]
    if (file && file.type.startsWith('image/')) {
      const fakeEvent = {
        target: { files: [file] }
      } as unknown as React.ChangeEvent<HTMLInputElement>
      await handleFileUpload(fakeEvent)
    }
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }

  const handleUrlInsert = () => {
    if (!imageUrl) {
      toast.error('Please enter an image URL')
      return
    }

    onImageSelect(imageUrl, imageAlt, imageWidth)
    closeDrawer('image-picker')
    setImageUrl('')
    setImageAlt('')
    setImageWidth('100%')
  }

  const handleGallerySelect = () => {
    if (!selectedImage) {
      toast.error('Please select an image')
      return
    }

    onImageSelect(selectedImage.original_url, selectedImage.alt_text || '', imageWidth)
    closeDrawer('image-picker')
    setSelectedImage(null)
    setImageWidth('100%')
  }

  const filteredGalleryImages = galleryImages.filter(img =>
    searchQuery === '' ||
    img.original_filename?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    img.alt_text?.toLowerCase().includes(searchQuery.toLowerCase())
  )

  return (
    <div className="flex flex-col h-full">
      <Tabs value={selectedTab} onValueChange={setSelectedTab} className="flex-1 flex flex-col">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="upload" className="flex items-center gap-2">
            <Upload className="h-4 w-4" />
            Upload
          </TabsTrigger>
          <TabsTrigger value="url" className="flex items-center gap-2">
            <ImageIcon className="h-4 w-4" />
            URL
          </TabsTrigger>
          <TabsTrigger value="recent" className="flex items-center gap-2">
            <Clock className="h-4 w-4" />
            Recent
          </TabsTrigger>
          <TabsTrigger value="gallery" className="flex items-center gap-2">
            <ImageIcon className="h-4 w-4" />
            Gallery
          </TabsTrigger>
        </TabsList>

        <TabsContent value="upload" className="flex-1 mt-4 space-y-4">
          <div className="space-y-2">
            <Label htmlFor="image-width-upload">Width</Label>
            <Select value={imageWidth} onValueChange={setImageWidth}>
              <SelectTrigger id="image-width-upload">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="25%">25% (Small)</SelectItem>
                <SelectItem value="50%">50% (Medium)</SelectItem>
                <SelectItem value="75%">75% (Large)</SelectItem>
                <SelectItem value="100%">100% (Full Width)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div
            className="border-2 border-dashed border-muted-foreground/50 rounded-lg p-8 text-center cursor-pointer hover:border-primary/50 transition-colors h-64 flex flex-col items-center justify-center"
            onClick={() => document.getElementById('image-upload')?.click()}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
          >
            <Upload className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-sm font-medium">Click to upload or drag and drop</p>
            <p className="text-xs text-muted-foreground mt-2">PNG, JPG, GIF, WebP up to 10MB</p>
            <input
              id="image-upload"
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleFileUpload}
              disabled={uploading}
            />
            {uploading && (
              <div className="mt-4">
                <Loader2 className="h-6 w-6 animate-spin mx-auto" />
                <p className="text-sm text-muted-foreground mt-2">Uploading...</p>
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="url" className="flex-1 mt-4 space-y-4">
          <div className="space-y-2">
            <Label htmlFor="image-url">Image URL</Label>
            <Input
              id="image-url"
              type="url"
              placeholder="https://example.com/image.jpg"
              value={imageUrl}
              onChange={(e) => setImageUrl(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="image-alt">Alt Text (optional)</Label>
            <Input
              id="image-alt"
              type="text"
              placeholder="Describe the image for accessibility"
              value={imageAlt}
              onChange={(e) => setImageAlt(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="image-width">Width</Label>
            <Select value={imageWidth} onValueChange={setImageWidth}>
              <SelectTrigger id="image-width">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="25%">25% (Small)</SelectItem>
                <SelectItem value="50%">50% (Medium)</SelectItem>
                <SelectItem value="75%">75% (Large)</SelectItem>
                <SelectItem value="100%">100% (Full Width)</SelectItem>
                <SelectItem value="custom">Custom</SelectItem>
              </SelectContent>
            </Select>
            {imageWidth === 'custom' && (
              <Input
                type="text"
                placeholder="Enter width (e.g., 400px, 60%)"
                onChange={(e) => setImageWidth(e.target.value)}
                className="mt-2"
              />
            )}
          </div>
          {imageUrl && (
            <div className="border rounded-lg p-4">
              <p className="text-sm font-medium mb-2">Preview:</p>
              <img
                src={imageUrl}
                alt={imageAlt || 'Preview'}
                className="max-w-full h-auto max-h-48 rounded"
                onError={(e) => {
                  e.currentTarget.style.display = 'none'
                  toast.error('Invalid image URL')
                }}
              />
            </div>
          )}
        </TabsContent>

        <TabsContent value="recent" className="flex-1 mt-4 space-y-4">
          <div className="space-y-2">
            <Label htmlFor="image-width-recent">Width</Label>
            <Select value={imageWidth} onValueChange={setImageWidth}>
              <SelectTrigger id="image-width-recent">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="25%">25% (Small)</SelectItem>
                <SelectItem value="50%">50% (Medium)</SelectItem>
                <SelectItem value="75%">75% (Large)</SelectItem>
                <SelectItem value="100%">100% (Full Width)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <ScrollArea className="h-[350px]">
            <div className="grid grid-cols-3 gap-4 p-1">
              {loading ? (
                <div className="col-span-3 text-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin mx-auto" />
                  <p className="text-sm text-muted-foreground mt-2">Loading recent images...</p>
                </div>
              ) : recentImages.length === 0 ? (
                <p className="col-span-3 text-center text-muted-foreground py-8">
                  No recent images
                </p>
              ) : (
                recentImages.map((image) => (
                  <div
                    key={image.id || image.original_url}
                    className={cn(
                      "border rounded-lg p-2 cursor-pointer hover:border-primary transition-colors",
                      selectedImage?.id === image.id && "border-primary bg-primary/10"
                    )}
                    onClick={() => setSelectedImage(image)}
                  >
                    <img
                      src={image.thumbnail_url || image.original_url}
                      alt={image.alt_text || image.original_filename}
                      className="w-full h-24 object-cover rounded"
                    />
                    <p className="text-xs truncate mt-1">{image.original_filename}</p>
                  </div>
                ))
              )}
            </div>
          </ScrollArea>
        </TabsContent>

        <TabsContent value="gallery" className="flex-1 mt-4">
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Search className="h-4 w-4 text-muted-foreground" />
              <Input
                type="search"
                placeholder="Search images..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="flex-1"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="image-width-gallery">Width</Label>
              <Select value={imageWidth} onValueChange={setImageWidth}>
                <SelectTrigger id="image-width-gallery">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="25%">25% (Small)</SelectItem>
                  <SelectItem value="50%">50% (Medium)</SelectItem>
                  <SelectItem value="75%">75% (Large)</SelectItem>
                  <SelectItem value="100%">100% (Full Width)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <ScrollArea className="h-[300px]">
              <div className="grid grid-cols-3 gap-4 p-1">
                {loading ? (
                  <div className="col-span-3 text-center py-8">
                    <Loader2 className="h-8 w-8 animate-spin mx-auto" />
                    <p className="text-sm text-muted-foreground mt-2">Loading gallery...</p>
                  </div>
                ) : filteredGalleryImages.length === 0 ? (
                  <p className="col-span-3 text-center text-muted-foreground py-8">
                    No images in gallery
                  </p>
                ) : (
                  filteredGalleryImages.map((image) => (
                    <div
                      key={image.id || image.original_url}
                      className={cn(
                        "border rounded-lg p-2 cursor-pointer hover:border-primary transition-colors",
                        selectedImage?.id === image.id && "border-primary bg-primary/10"
                      )}
                      onClick={() => setSelectedImage(image)}
                    >
                      <img
                        src={image.thumbnail_url || image.original_url}
                        alt={image.alt_text || image.original_filename}
                        className="w-full h-24 object-cover rounded"
                      />
                      <p className="text-xs truncate mt-1">{image.original_filename}</p>
                    </div>
                  ))
                )}
              </div>
            </ScrollArea>
          </div>
        </TabsContent>
      </Tabs>

      <div className="border-t pt-4 flex justify-end gap-2">
        <Button variant="outline" onClick={() => closeDrawer('image-picker')}>
          Cancel
        </Button>
        {selectedTab === 'url' ? (
          <Button onClick={handleUrlInsert} disabled={!imageUrl}>
            Insert Image
          </Button>
        ) : (selectedTab === 'recent' || selectedTab === 'gallery') ? (
          <Button onClick={handleGallerySelect} disabled={!selectedImage}>
            Use Selected Image
          </Button>
        ) : null}
      </div>
    </div>
  )
}